import { Link, useNavigate } from 'react-router-dom';
import { FlaskConical, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ScientificAnalysis } from '../types/analysis';
import { analysisTypeLabel } from '../types/analysis';
import { useEvents } from '../context/EventsContext';
import { useAnalysis } from '../context/AnalysisContext';
import { DashPanel } from '../components/DashPanel';
import { EntityActionsMenu } from '../components/EntityActionsMenu';

interface AnalysisCardProps {
  analysis: ScientificAnalysis;
}

export function AnalysisCard({ analysis }: AnalysisCardProps) {
  const { events } = useEvents();
  const { deleteAnalysis } = useAnalysis();
  const navigate = useNavigate();
  const linked = events.filter((e) => analysis.eventIds.includes(e.id));
  const created = format(parseISO(analysis.createdAt), 'dd/MM/yyyy', { locale: ptBR });

  return (
    <Link to={`/dashboard/analysis/${analysis.id}`} className="block group h-full relative">
      <EntityActionsMenu
        className="absolute top-2 right-2 z-10"
        onEdit={() => navigate(`/dashboard/analysis/${analysis.id}/edit`)}
        onDelete={() => deleteAnalysis(analysis.id)}
        deleteTitle="Excluir análise"
        deleteDescription={`Tem certeza que deseja excluir "${analysis.title}"? Essa ação não pode ser desfeita.`}
      />
      <DashPanel padding="sm" className="h-full hover:border-primary/40 transition-colors">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <FlaskConical className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-medium uppercase tracking-wide text-primary">
              {analysisTypeLabel(analysis)}
            </span>
            <h3 className="text-sm font-semibold text-foreground mt-1 line-clamp-2">
              {analysis.title}
            </h3>
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
              {linked.length > 0
                ? linked.map((e) => e.title).join(' · ')
                : analysis.types.includes('geral')
                  ? 'Todos os eventos'
                  : 'Sem eventos'}
            </p>
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-border text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {created}
              </span>
              <span
                className={
                  analysis.status === 'concluida'
                    ? 'text-primary font-medium'
                    : 'text-muted-foreground'
                }
              >
                {analysis.status === 'concluida' ? 'Concluída' : 'Rascunho'}
              </span>
            </div>
          </div>
        </div>
      </DashPanel>
    </Link>
  );
}
